import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError } from './modelInputContract';
import type { IndicatorId } from './titration';
import { formatSig } from './titrationExplore';
import { CONCORDANCE_TOLERANCE_ML, ML_PER_L, findConcordantTitres, molarMassOf, type FlowStep } from './volumetricAnalysis';
import { ACID_BASE_TITRATIONS, type DirectTitrationSpec } from './volumetricAnalysisData';

/**
 * Explore view of Volumetric Analysis (CT-INTERACT, chemistry/CLAUDE.md "Interactive calculation
 * standard"). The learner types the standard's mass and flask volume (standardisation) or the
 * titrant concentration (direct titration), the aliquot and 3–5 titre readings. Concordance uses
 * the reviewed `findConcordantTitres` rule (within 0.10 mL, rough titre never averaged); the
 * equations, mole ratios and default numbers come from the reviewed cases in
 * `volumetricAnalysisData.ts`.
 */
export const MAX_TITRE_ML = 50;
export const TITRE_KEYS = ['titre1', 'titre2', 'titre3', 'titre4', 'titre5'] as const;
export type TitreKey = (typeof TITRE_KEYS)[number];
export const MIN_RUNS = 3;
export const MAX_RUNS = 5;

export interface VolumetricExploreSetup extends Record<TitreKey, number> {
  standardMassG: number;
  flaskMl: number;
  aliquotMl: number;
  titrantM: number;
  /** How many titre readings are used (3–5); the first is always the rough titre. */
  runs: number;
}

export type VolumetricExploreInput = Exclude<keyof VolumetricExploreSetup, 'runs'>;
export type VolumetricMethod = 'standardise' | 'direct';

const TITRE_LABELS: Record<TitreKey, string> = {
  titre1: 'Rough titre',
  titre2: 'Titre 2',
  titre3: 'Titre 3',
  titre4: 'Titre 4',
  titre5: 'Titre 5',
};

const FIELD_LIST: Record<VolumetricExploreInput, ModelField<VolumetricExploreInput>> = {
  standardMassG: { key: 'standardMassG', label: 'Mass of primary standard', unit: 'g', min: 0.1, max: 20, step: 0.001 },
  flaskMl: { key: 'flaskMl', label: 'Volumetric flask volume', unit: 'mL', min: 50, max: 1000, step: 0.1 },
  aliquotMl: { key: 'aliquotMl', label: 'Aliquot volume', unit: 'mL', min: 5, max: 50, step: 0.01 },
  titrantM: { key: 'titrantM', label: 'Titrant concentration', unit: 'mol/L', min: 0.01, max: 1, step: 0.0001 },
  ...(Object.fromEntries(
    TITRE_KEYS.map((key) => [key, { key, label: TITRE_LABELS[key], unit: 'mL', min: 0, max: MAX_TITRE_ML, step: 0.01 }]),
  ) as Record<TitreKey, ModelField<VolumetricExploreInput>>),
};

export interface VolumetricExploreSystem {
  /** Stable id. Never rename. */
  id: string;
  name: string;
  method: VolumetricMethod;
  /** The reviewed case this system reuses for its equation, ratio, indicator and default titres. */
  problemId: string;
  /** Formula for the molar mass of a weighed primary standard (standardisation only). */
  standardFormula?: string;
  /** Indicator colour used by the 3D flask, and how far through its change the end point sits. */
  indicator: IndicatorId;
  endpointShade: number;
  defaults: VolumetricExploreSetup;
}

const titres = (values: number[]) =>
  Object.fromEntries(TITRE_KEYS.map((key, index) => [key, values[index] ?? values[values.length - 1]])) as Record<TitreKey, number>;

/**
 * Defaults reproduce the reviewed cases: 1.325 g Na₂CO₃ in 250.0 mL is the 0.05000 mol L⁻¹
 * standard of `ab-standardise-hcl`; 2.042 g KHP in 100.0 mL is `std-khp-concentration`.
 */
export const VOLUMETRIC_EXPLORE_SYSTEMS: VolumetricExploreSystem[] = [
  {
    id: 'standardise-hcl-carbonate',
    name: 'Standardise HCl with Na₂CO₃',
    method: 'standardise',
    problemId: 'ab-standardise-hcl',
    standardFormula: 'Na2CO3',
    indicator: 'methyl-orange',
    endpointShade: 0.5,
    defaults: { standardMassG: 1.325, flaskMl: 250, aliquotMl: 20, titrantM: 0.1, runs: 5, ...titres([18.6, 18.15, 18.3, 18.1, 18.2]) },
  },
  {
    id: 'standardise-naoh-khp',
    name: 'Standardise NaOH with KHP',
    method: 'standardise',
    problemId: 'ab-standardise-naoh',
    standardFormula: 'KHC8H4O4',
    indicator: 'phenolphthalein',
    endpointShade: 0.3,
    defaults: { standardMassG: 2.042, flaskMl: 100, aliquotMl: 25, titrantM: 0.1, runs: 5, ...titres([24.2, 23.65, 23.7, 23.6, 23.95]) },
  },
  {
    id: 'direct-hcl-naoh',
    name: 'HCl of unknown strength',
    method: 'direct',
    problemId: 'ab-hcl-naoh',
    indicator: 'phenolphthalein',
    endpointShade: 0.3,
    defaults: { standardMassG: 1.325, flaskMl: 250, aliquotMl: 20, titrantM: 0.1, runs: 5, ...titres([22.8, 21.9, 21.85, 21.95, 21.88]) },
  },
  {
    id: 'direct-sulfuric-naoh',
    name: 'H₂SO₄, a diprotic acid',
    method: 'direct',
    problemId: 'ab-sulfuric',
    indicator: 'phenolphthalein',
    endpointShade: 0.3,
    defaults: { standardMassG: 1.325, flaskMl: 250, aliquotMl: 25, titrantM: 0.12, runs: 4, ...titres([24.1, 23.55, 23.6, 23.5]) },
  },
];

export function exploreSpec(system: VolumetricExploreSystem): DirectTitrationSpec {
  const problem = ACID_BASE_TITRATIONS.find((candidate) => candidate.id === system.problemId);
  if (!problem || problem.spec.kind !== 'direct') throw new Error(`Missing reviewed case ${system.problemId}.`);
  return problem.spec;
}

/** The input fields this system uses, in display order: setup first, then the titres in use. */
export function volumetricExploreFields(system: VolumetricExploreSystem, runs: number): ModelField<VolumetricExploreInput>[] {
  const setup: VolumetricExploreInput[] =
    system.method === 'standardise' ? ['standardMassG', 'flaskMl', 'aliquotMl'] : ['titrantM', 'aliquotMl'];
  return [...setup, ...TITRE_KEYS.slice(0, runs)].map((key) => FIELD_LIST[key]);
}

export interface VolumetricExploreResult {
  steps: WorkedStep[];
  flow: FlowStep[];
  /** Titre readings in use, index 0 = rough. */
  titres: number[];
  concordant: number[];
  meanTitreMl: number;
  standardM: number | null;
  unknownLabel: string;
  unknownM: number;
}

const mlText = (ml: number) => `${ml.toFixed(2)} mL`;
const litreText = (ml: number) => `${(ml / ML_PER_L).toFixed(5)} L`;
const molText = (value: number) => `${formatSig(value, 4)} mol`;
const molarText = (value: number) => `${formatSig(value, 4)} mol/L`;
const runName = (index: number) => (index === 0 ? 'rough' : `titre ${index + 1}`);

/**
 * Concordance, mean titre, moles, mole ratio and the unknown concentration for one learner setup.
 * Throws on invalid input and when no two accurate titres agree within 0.10 mL (fail closed).
 */
export function calculateVolumetricExplore(system: VolumetricExploreSystem, setup: VolumetricExploreSetup): VolumetricExploreResult {
  if (!Number.isInteger(setup.runs) || setup.runs < MIN_RUNS || setup.runs > MAX_RUNS) {
    throw new Error(`Use ${MIN_RUNS}–${MAX_RUNS} titre readings.`);
  }
  const fields = volumetricExploreFields(system, setup.runs);
  const error = firstModelError(fields, setup);
  if (error) throw new Error(error);
  const spec = exploreSpec(system);
  const readings = TITRE_KEYS.slice(0, setup.runs).map((key) => setup[key]);
  const concordant = findConcordantTitres(readings);
  if (!concordant) {
    throw new Error(
      `No two accurate titres agree within ${CONCORDANCE_TOLERANCE_ML.toFixed(2)} mL. Repeat the titration before averaging.`,
    );
  }
  const mean = concordant.average;
  const { titrant, analyte, coefficients } = spec;
  const steps: WorkedStep[] = [];
  const flow: FlowStep[] = [];

  let standardM: number | null = null;
  if (system.method === 'standardise') {
    const molarMass = system.standardFormula ? molarMassOf(system.standardFormula) : null;
    if (!molarMass) throw new Error('No reviewed molar mass is available for this standard.');
    const standardMol = setup.standardMassG / molarMass;
    standardM = standardMol / (setup.flaskMl / ML_PER_L);
    steps.push({
      id: 'standard',
      title: `Make the ${analyte.formula} standard`,
      formula: 'c = (m ÷ M) ÷ V',
      working: `n = ${setup.standardMassG} g ÷ ${molarMass.toFixed(2)} g/mol = ${molText(standardMol)}; c = ${molText(standardMol)} ÷ ${litreText(setup.flaskMl)} = ${molarText(standardM)}`,
      status: 'done',
    });
    flow.push({ label: `c(${analyte.formula}) = m ÷ M ÷ V`, value: molarText(standardM) });
  }

  const used = concordant.indices.map((index) => readings[index]);
  const left = readings
    .map((value, index) => ({ value, index }))
    .filter(({ index }) => index > 0 && !concordant.indices.includes(index));
  steps.push({
    id: 'concordance',
    title: 'Concordant titres',
    formula: `agree within ${CONCORDANCE_TOLERANCE_ML.toFixed(2)} mL; the rough titre is never averaged`,
    working: `Rough ${mlText(readings[0])} left out${left.length ? `; ${left.map(({ value, index }) => `${runName(index)} ${mlText(value)}`).join(', ')} also left out` : ''}; ${used.map((value) => value.toFixed(2)).join(', ')} mL agree (spread ${mlText(concordant.spread)})`,
    status: 'done',
  });
  steps.push({
    id: 'mean',
    title: 'Mean titre',
    formula: 'mean = sum ÷ number of concordant titres',
    working: `(${used.map((value) => value.toFixed(2)).join(' + ')}) ÷ ${used.length} = ${mlText(mean)}`,
    status: 'done',
  });
  flow.push({ label: 'mean titre', value: mlText(mean) });

  const ratioText = `${titrant.formula} : ${analyte.formula} = ${coefficients.titrant} : ${coefficients.analyte}`;
  let unknownLabel: string;
  let unknownM: number;
  if (system.method === 'standardise' && standardM !== null) {
    const analyteMol = standardM * (setup.aliquotMl / ML_PER_L);
    const titrantMol = (analyteMol * coefficients.titrant) / coefficients.analyte;
    unknownLabel = `c(${titrant.formula})`;
    unknownM = titrantMol / (mean / ML_PER_L);
    steps.push(
      {
        id: 'moles',
        title: `Moles of ${analyte.formula} in the aliquot`,
        formula: 'n = c × V',
        working: `n(${analyte.formula}) = ${molarText(standardM)} × ${litreText(setup.aliquotMl)} = ${molText(analyteMol)}`,
        status: 'done',
      },
      {
        id: 'ratio',
        title: 'Mole ratio',
        formula: ratioText,
        working: `n(${titrant.formula}) = ${molText(analyteMol)} × ${coefficients.titrant}/${coefficients.analyte} = ${molText(titrantMol)}`,
        status: 'done',
      },
      {
        id: 'unknown',
        title: `Concentration of ${titrant.formula}`,
        formula: 'c = n ÷ V(mean titre)',
        working: `${unknownLabel} = ${molText(titrantMol)} ÷ ${litreText(mean)} = ${molarText(unknownM)}`,
        status: 'done',
      },
    );
    flow.push(
      { label: `n(${analyte.formula}) = cV`, value: molText(analyteMol) },
      { label: `× ${coefficients.titrant}/${coefficients.analyte}`, value: molText(titrantMol) },
      { label: '÷ mean titre', value: molarText(unknownM) },
    );
  } else {
    const titrantMol = setup.titrantM * (mean / ML_PER_L);
    const analyteMol = (titrantMol * coefficients.analyte) / coefficients.titrant;
    unknownLabel = `c(${analyte.formula})`;
    unknownM = analyteMol / (setup.aliquotMl / ML_PER_L);
    steps.push(
      {
        id: 'moles',
        title: `Moles of ${titrant.formula} delivered`,
        formula: 'n = c × V(mean titre)',
        working: `n(${titrant.formula}) = ${setup.titrantM} mol/L × ${litreText(mean)} = ${molText(titrantMol)}`,
        status: 'done',
      },
      {
        id: 'ratio',
        title: 'Mole ratio',
        formula: ratioText,
        working: `n(${analyte.formula}) = ${molText(titrantMol)} × ${coefficients.analyte}/${coefficients.titrant} = ${molText(analyteMol)}`,
        status: 'done',
      },
      {
        id: 'unknown',
        title: `Concentration of ${analyte.formula}`,
        formula: 'c = n ÷ V(aliquot)',
        working: `${unknownLabel} = ${molText(analyteMol)} ÷ ${litreText(setup.aliquotMl)} = ${molarText(unknownM)}`,
        status: 'done',
      },
    );
    flow.push(
      { label: `n(${titrant.formula}) = cV`, value: molText(titrantMol) },
      { label: `× ${coefficients.analyte}/${coefficients.titrant}`, value: molText(analyteMol) },
      { label: '÷ aliquot', value: molarText(unknownM) },
    );
  }

  return { steps, flow, titres: readings, concordant: concordant.indices, meanTitreMl: mean, standardM, unknownLabel, unknownM };
}

/** Learning question of the Volumetric Analysis Explore view (CT-INTERACT C7). */
export const VOLUMETRIC_QUESTION = 'Which titres can you trust, and how do they give the concentration of the unknown?';

/** An accurate titre this far from the mean is an outlier, mL. */
const OUTLIER_ML = 0.5;
/** Target concentration of the standardisation task, mol/L, and its band (± 0.5 %). */
const TARGET_TITRANT_M = 0.1;
const TARGET_TOLERANCE = 0.005;
const FLOAT_EPSILON = 1e-9;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the model result, never the UI. */
export const VOLUMETRIC_CHALLENGES: ExploreChallenge<VolumetricExploreResult>[] = [
  {
    id: 'rough-agrees',
    prompt: 'Change the rough titre so it is within 0.10 mL of the mean titre. Is it averaged now?',
    hint: 'Type a rough titre close to the mean titre in the working.',
    isMet: ({ titres, meanTitreMl }) => Math.abs(titres[0] - meanTitreMl) <= CONCORDANCE_TOLERANCE_ML + FLOAT_EPSILON,
    why: 'No. The rough titre is a fast first run to find where the end point is, so it is never averaged, even when it happens to agree.',
  },
  {
    id: 'outlier',
    prompt: 'Make one accurate titre at least 0.50 mL away from the others. What happens to the mean?',
    hint: 'Change titre 2 by a large amount, for example to 19.20 mL.',
    isMet: ({ titres, concordant, meanTitreMl }) =>
      titres.some((value, index) => index > 0 && !concordant.includes(index) && Math.abs(value - meanTitreMl) >= OUTLIER_ML - FLOAT_EPSILON),
    why: 'The outlier is left out, so it cannot drag the mean. Averaging every titre would let one bad run (an overshot end point or an air bubble) shift the answer; only titres within 0.10 mL agree.',
  },
  {
    id: 'target-concentration',
    prompt: 'Standardise a titrant: change the standard or the titres so its concentration is 0.1000 mol/L (± 0.5 %).',
    hint: 'Work backwards: mean titre = n(titrant) ÷ 0.1000 mol/L. Remember the mole ratio.',
    isMet: ({ standardM, unknownM }) =>
      standardM !== null && Math.abs(unknownM - TARGET_TITRANT_M) <= TARGET_TOLERANCE * TARGET_TITRANT_M + FLOAT_EPSILON,
    why: 'c(titrant) = n ÷ V(mean titre), so a larger titre means a more dilute titrant. With Na₂CO₃ the aliquot needs 2 × 1.000 × 10⁻³ mol HCl (2 : 1 ratio), so 0.1000 mol/L needs a 20.00 mL mean titre.',
  },
];
